import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../../core/i18n'
import * as constants from '../../../../../static/constants'
import injectGoogleMaps from '../../../../../utils/injectGoogleMaps'
import { AUTOCOMPLETE_FIELDS, isValidPlace } from 'utils/autocomplete'

interface IPickupLocationChange {
  latitude: number | null
  longitude: number | null
  pickup_location: string
  selectionError: boolean
}

interface IProps {
  google: any
  hideTitle?: boolean
  initPickupInfo?: {
    latitude?: string | null
    longitude?: string | null
    pickup_location?: string | null
    selectionError?: boolean
  }
  onPickupLocationChange?(pickupInfo: IPickupLocationChange): void
}

export const ReservationPickupLocation: React.FC<IProps> = props => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [map, setMap] = React.useState(null)
  const [marker, setMarker] = React.useState(null)
  const [coordinate, setCoordinate] = React.useState({
    latitude: props?.initPickupInfo?.latitude || null,
    longitude: props?.initPickupInfo?.longitude || null,
  })

  const [pickupLocation, setPickupLocation] = React.useState(
    props.initPickupInfo?.pickup_location || ''
  )

  const selectionError = I18n.t('reservation.pickup_location_selection_required')
  const [error, setError] = React.useState<string | null>(
    pickupLocation.trim() &&
      (props.initPickupInfo?.selectionError ??
        (coordinate.latitude === null || coordinate.longitude === null))
      ? selectionError
      : null
  )

  React.useEffect(() => {
    if (props.google) {
      return createAutoComplete()
    }
  }, [props.google])

  React.useEffect(() => {
    const { latitude, longitude } = coordinate

    if (props.google && latitude && longitude) {
      const latLng = new props.google.maps.LatLng(latitude, longitude)

      if (map) {
        map.panTo(latLng)
        map.setZoom(15)
      } else {
        createMap(latLng)
      }

      if (marker) {
        marker.setPosition(latLng)
      }
    }
  }, [props.google, coordinate])

  React.useEffect(() => {
    props.onPickupLocationChange?.({
      latitude: coordinate.latitude === null ? null : Number(coordinate.latitude),
      longitude: coordinate.longitude === null ? null : Number(coordinate.longitude),
      pickup_location: pickupLocation,
      selectionError: Boolean(error),
    })
  }, [coordinate, pickupLocation, error, props.onPickupLocationChange])

  const createMap = async latLng => {
    const mapCanvas = document.getElementById('pickup_location_map')

    if (!mapCanvas) {
      return
    }

    const Options = {
      zoom: 15,
      center: latLng,
      mapTypeId: 'roadmap',
    }
    const initializedMap = new props.google.maps.Map(mapCanvas, Options)
    const defaultMarker = new props.google.maps.Marker({
      position: latLng,
      title: 'pickup_location',
      map: initializedMap,
    })

    setMarker(defaultMarker)
    setMap(initializedMap)
  }

  const createAutoComplete = () => {
    const input = inputRef.current

    if (!input) {
      return
    }

    const autocomplete = new props.google.maps.places.Autocomplete(input, {
      fields: AUTOCOMPLETE_FIELDS,
    })
    const onPlaceChanged = () => {
      const place = autocomplete.getPlace()
      const latitude = place?.geometry?.location?.lat() ?? null
      const longitude = place?.geometry?.location?.lng() ?? null
      setPickupLocation(input.value)
      setCoordinate(
        input.value.trim() ? { latitude, longitude } : { latitude: null, longitude: null }
      )
      setError(input.value.trim() && !isValidPlace(place) ? selectionError : null)
    }

    const listener = autocomplete.addListener('place_changed', onPlaceChanged)
    return () => listener.remove()
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setPickupLocation(value)
    setCoordinate({ latitude: null, longitude: null })
    setError(null)
  }

  return (
    <Wrapper>
      {!props.hideTitle && (
        <h2 className="Editor_Title">{I18n.t('reservation.pickup_location')}</h2>
      )}
      <AutoComplete>
        <input
          id="pickup_location_search"
          type="text"
          name="pickup_location"
          ref={inputRef}
          defaultValue={props?.initPickupInfo?.pickup_location || ''}
          onChange={handleInputChange}
          aria-describedby={error ? 'pickup_location_error' : undefined}
        />
      </AutoComplete>
      <p className="note">{I18n.t('reservation.reservation_pickup_location_note')}</p>
      {error && (
        <p id="pickup_location_error" className="location_error" role="alert">
          {error}
        </p>
      )}
      <div
        id="pickup_location_map"
        className="map"
        style={{ display: coordinate.latitude && coordinate.longitude ? 'block' : 'none' }}
      />
      <input type="hidden" name="pickup_latitude" value={coordinate.latitude ?? ''} />
      <input type="hidden" name="pickup_longitude" value={coordinate.longitude ?? ''} />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .FormItmes {
    margin-top: 16px;
    .subtitle {
      margin-bottom: 0.4rem;
    }
  }

  .map {
    height: 280px;
    margin-top: 12px;
    border-radius: 1.2rem;
  }

  .note {
    color: ${constants.COLORS.subText};
    font-size: 12px;
    margin-top: 0.4rem;
    white-space: pre-line;
  }
  .location_error {
    color: ${constants.COLORS.caution};
    font-size: 13px;
    font-weight: 700;
    margin-top: 0.4rem;
  }
`

const AutoComplete = styled.div`
  > input {
    display: block;
    width: 100%;
    padding: 1.6rem 2.4rem;
    border: solid 1px ${constants.COLORS.divider};
    border-radius: 1.2rem;
    font-size: 1.6rem;
    transition: border 0.2s ease;
    outline: none;
    background: none;
    margin-top: 1.2rem;
    transition: border-color 0.2s;
    &:focus {
      border-color: ${constants.COLORS.primary};
    }
  }
`

export default injectGoogleMaps(ReservationPickupLocation)
